// Injected Solana wallet (Phantom, Solflare, Backpack). Keeps { address } in sync with the wallet and turns
// wallet errors into plain sentences. The connected wallet is the account: Shilled never asks for a sign-in.
// Transactions are built by the server; the wallet only signs (signTransaction) and the server sends them.

export const state = { address: null, ready: false };
const listeners = new Set();
const FORGET_KEY = "shilled.walletForgotten";

export class WalletError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "WalletError";
    this.code = code;
    this.friendly = true;
  }
}

export const NO_WALLET_MESSAGE = "No Solana wallet found. Install Phantom, Solflare or Backpack, then reload this page. On a phone, open this page in your wallet app's browser.";

const store = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  del(k) { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};

/** The injected provider: Phantom first, then Solflare, Backpack, or any wallet that sets window.solana. */
export function provider() {
  if (typeof window === "undefined") return undefined;
  return (window.phantom && window.phantom.solana) || window.solflare || window.backpack || window.solana || undefined;
}
export const hasWallet = () => !!provider();

export function onWallet(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit() {
  for (const fn of listeners) {
    try { fn(state); } catch (e) { console.error(e); }
  }
}

const keyOf = (r, p) => {
  const k = (r && r.publicKey) || (p && p.publicKey);
  return k ? k.toString() : null;
};

let initialised = false;
export async function initWallet() {
  if (initialised) return state;
  initialised = true;
  const p = provider();
  if (!p) {
    state.ready = true;
    emit();
    return state;
  }
  if (typeof p.on === "function") {
    p.on("accountChanged", (k) => { state.address = k && !store.get(FORGET_KEY) ? k.toString() : null; emit(); });
    p.on("disconnect", () => { state.address = null; emit(); });
  }
  try {
    // Reconnects silently only if the user already trusted this site.
    if (!store.get(FORGET_KEY)) state.address = keyOf(await p.connect({ onlyIfTrusted: true }), p);
  } catch { /* not trusted yet: stay disconnected */ }
  state.ready = true;
  emit();
  return state;
}

/** Asks the wallet for an account (opens the wallet). */
export async function connect() {
  const p = provider();
  if (!p) throw new WalletError("NO_WALLET", NO_WALLET_MESSAGE);
  const address = keyOf(await p.connect(), p);
  if (!address) throw new WalletError("NO_ACCOUNT", "Your wallet didn't share an account. Unlock it and try again.");
  store.del(FORGET_KEY);
  state.address = address;
  emit();
  return address;
}

/** Forgets the wallet on this site and asks the wallet to disconnect. */
export async function forget() {
  store.set(FORGET_KEY, "1");
  state.address = null;
  emit();
  try { await provider()?.disconnect(); } catch { /* not supported */ }
}

/**
 * Signs a server-built transaction (base64, serialized without signatures) and returns it signed (base64).
 * The server checks it, sends it and waits for confirmation.
 */
export async function signTransaction(base64, Transaction) {
  const p = provider();
  if (!p) throw new WalletError("NO_WALLET", NO_WALLET_MESSAGE);
  if (!state.address) await connect();
  const tx = Transaction.deserialize(Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)));
  const signed = await p.signTransaction(tx);
  const bytes = signed.serialize();
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

export function friendlyError(e) {
  if (!e) return "Something went wrong. Try again.";
  if (e.friendly && e.message) return e.message;
  const msg = String(e.message || e);
  if (e.code === 4001 || /reject|denied|cancel/i.test(msg)) return "You cancelled it in your wallet.";
  if (/insufficient|not enough|0x1\b/i.test(msg)) return "Not enough SOL in this wallet for that, including the network fee.";
  if (/blockhash|expired/i.test(msg)) return "That took too long and expired. Try again.";
  return "The wallet returned an error. Try again.";
}

/**
 * Signs several server-built transactions (base64) with one wallet approval and returns them signed (base64).
 * The server checks they are the ones it built, then sends them in order.
 */
export async function signAllTransactions(base64s) {
  const p = provider();
  if (!p) throw new WalletError("NO_WALLET", NO_WALLET_MESSAGE);
  if (!state.address) await connect();
  const { VersionedTransaction } = await import("@solana/web3.js");
  const txs = base64s.map((b) => VersionedTransaction.deserialize(Uint8Array.from(atob(b), (c) => c.charCodeAt(0))));
  const live = p.publicKey ? p.publicKey.toString() : state.address;
  if (live !== state.address) throw new WalletError("WRONG_ACCOUNT", "Your wallet switched accounts. Reconnect and try again.");
  const signed = txs.length === 1 ? [await p.signTransaction(txs[0])] : await p.signAllTransactions(txs);
  return signed.map((t) => {
    const bytes = t.serialize();
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
  });
}
